import React, { createContext, useContext, useState, ReactNode } from "react";
import { ActionButton, Step } from "./types";

interface FastActionsContextType {
   modalStep: Step | null;
   setModalStep: (value: Step) => void;
   actions: ActionButton[];
   setActions: (value: ActionButton[]) => void;
   formStates: any;
   setIsFormOpen: (id: number, value: boolean) => void;
   isFormOpen: (value: number) => boolean;
   tabsVisibility: Map<string, boolean>;
   setTabsVisibility: (value: Map<string, boolean>) => void;
   isFormLoading: boolean;
   setIsFormLoading: (value: boolean) => void;
   hasRequiredFields: boolean;
   setHasRequiredFields: (value: boolean) => void;
}

const FastActionsContext = createContext<FastActionsContextType | undefined>(undefined);

interface FatsActionsProviderProps {
   children: ReactNode;
}

export const FastActionsProvider: React.FC<FatsActionsProviderProps> = ({ children }) => {
   const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
   const [formStates, setFormStates] = useState<Map<number, boolean>>(new Map());
   const [modalStep, setModalStep] = useState<Step | null>(null);
   const [actions, setActions] = useState<ActionButton[]>([]);
   const [tabsVisibility, setTabsVisibility] = useState<Map<string, boolean>>(new Map());
   const [hasRequiredFields, setHasRequiredFields] = useState<boolean>(true);

   const setIsFormOpen = (id: number, value: boolean) => {
      if (id == null) return;
      setFormStates((prev) => {
         const newMap = new Map(prev);
         newMap.set(id, value);
         return newMap;
      });
   };

   const isFormOpen = (id: number) => {
      if (id == null) return false;
      return formStates.get(id) ?? false;
   };

   return (
      <FastActionsContext.Provider
         value={{
            modalStep,
            setModalStep,
            actions,
            setActions,
            formStates,
            setIsFormOpen,
            isFormOpen,
            tabsVisibility,
            setTabsVisibility,
            isFormLoading,
            setIsFormLoading,
            hasRequiredFields,
            setHasRequiredFields,
         }}
      >
         {children}
      </FastActionsContext.Provider>
   );
};

export const useFastActionsContext = (): FastActionsContextType => {
   const context = useContext(FastActionsContext);
   if (!context) {
      throw new Error("useFastActionsContext must be used within a FastActionsProvider");
   }
   return context;
};

export const useFormState = () => {
   const context = useContext(FastActionsContext);

   if (!context) {
      throw new Error("useFormState must be used within a FastActionsProvider");
   }

   const { isFormOpen, setIsFormOpen } = context;
   return {
      isFormOpen,
      setIsFormOpen: (id: number, value: boolean) => setIsFormOpen(id, value),
   };
};
